'use server'

import { apiRequest, ApiResponse } from '../request'
import { downloadBlob } from '../../download-blob'

export interface Campaign {
    id: number
    name: string
    description: string | null
    status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'FINISHED'
    defaultDestinationType: 'WHATSAPP' | 'URL' | 'WHATSAPP_WITH_MESSAGE'
    defaultDestinationValue: string | null
    defaultMessage: string | null
    startDate: string | null
    endDate: string | null
    budget: number | null
    companyId: number
    createdAt: string
    updatedAt: string
    links?: CampaignLink[]
    totalLinks?: number
    totalClicks?: number
    totalConversions?: number
    totalRevenue?: number
}

export interface CampaignLink {
    id: number
    campaignId: number
    source: string
    medium: string | null
    content: string | null
    destinationType: string | null
    destinationValue: string | null
    message: string | null
    internalCode: string
    shortCode: string | null
    /** Endereço que vai no anúncio (domínio dos links curtos + código). */
    publicUrl?: string
    /** Cliques do link (vem no detalhe da campanha). */
    clickCount?: number
    bitlyUrl: string | null
    active: boolean
    createdAt: string
}

/**
 * Endereço público do link. Vem pronto do backend (`publicUrl`); o fallback só cobre
 * resposta antiga. O redirecionamento (`/c/:code`) é rota da API, não do app.
 */
export function getCampaignLinkUrl(link: Pick<CampaignLink, 'publicUrl' | 'shortCode' | 'internalCode'>): string {
    if (link.publicUrl) return link.publicUrl
    const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
    return `${base}/c/${link.shortCode ?? link.internalCode}`
}

export interface CampaignStats {
    campaign: { id: number; name: string; status: string }
    totalClicks: number
    totalConversions: number
    totalRevenue: number
    conversionRate: string
    bySource: {
        source: string
        medium: string | null
        clicks: number
        conversions: number
        revenue: number
        conversionRate: string
    }[]
    byType: {
        type: string
        count: number
        revenue: number
    }[]
}

export interface CreateCampaignData {
    name: string
    description?: string
    status?: string
    defaultDestinationType: string
    defaultDestinationValue?: string
    defaultMessage?: string
    startDate?: string
    endDate?: string
    budget?: number
}

export interface CreateCampaignLinkData {
    source: string
    medium?: string
    content?: string
    destinationType?: string
    destinationValue?: string
    message?: string
}

const BASE = 'marketing/campaigns'

export async function getCampaigns(filters?: {
    status?: string
    search?: string
    page?: number
    limit?: number
}): Promise<ApiResponse<{ data: Campaign[]; total: number; page: number; limit: number; totalPages: number }>> {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    if (filters?.search) params.set('search', filters.search)
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    const query = params.toString()
    return apiRequest(`${BASE}${query ? `?${query}` : ''}`, {})
}

export async function getCampaign(id: number): Promise<ApiResponse<Campaign>> {
    return apiRequest<Campaign>(`${BASE}/${id}`, {})
}

export async function createCampaign(data: CreateCampaignData): Promise<ApiResponse<Campaign>> {
    return apiRequest<Campaign>(BASE, { data }, 'post')
}

export async function updateCampaign(id: number, data: Partial<CreateCampaignData>): Promise<ApiResponse<Campaign>> {
    return apiRequest<Campaign>(`${BASE}/${id}`, { data }, 'patch')
}

export async function deleteCampaign(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}

export async function addCampaignLink(campaignId: number, data: CreateCampaignLinkData): Promise<ApiResponse<CampaignLink>> {
    return apiRequest<CampaignLink>(`${BASE}/${campaignId}/links`, { data }, 'post')
}

/** Baixa o QR code do link em PNG (via blob, para levar o JWT). */
export async function downloadCampaignLinkQrCode(campaignId: number, link: Pick<CampaignLink, 'id' | 'shortCode' | 'source'>): Promise<void> {
    const { getClient } = await import('@/lib/api/client')
    const client = await getClient()
    const response = await client.get(`${BASE}/${campaignId}/links/${link.id}/qrcode.png`, { responseType: 'blob' })
    downloadBlob(new Blob([response.data], { type: 'image/png' }), `qrcode-${link.source}-${link.shortCode ?? link.id}.png`)
}

export async function removeCampaignLink(campaignId: number, linkId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${campaignId}/links/${linkId}`, {}, 'delete')
}

export async function getCampaignStats(id: number): Promise<ApiResponse<CampaignStats>> {
    return apiRequest<CampaignStats>(`${BASE}/${id}/stats`, {})
}

